import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DetailHeader } from '@/components/common/DetailHeader'
import { useContract, useContractFormSchema } from './hooks'
import { StageStepperHeader } from './components/StageStepperHeader'
import { SectionTabs } from './components/SectionTabs'
import { ContractPartiesPanel } from './components/parties/ContractPartiesPanel'
import { ElementsTab } from './elements/ElementsTab'
import { useContractElements } from './elements/hooks'
import { ContractTasksTab } from '@/features/tasks/components/ContractTasksTab'
import { TaskSidebar } from './components/TaskSidebar'
import { ContractSidebarSections } from './components/ContractSidebarSections'
import { CancelContractDialog } from './components/CancelContractDialog'
import { FinancingTab } from './financing/FinancingTab'
import { StatusesTab } from './statuses/StatusesTab'
import { FilesTab } from './files/FilesTab'
import { AnnexesTab } from './annexes/AnnexesTab'
import { AnnexBanner } from './annexes/components/AnnexBanner'
import { useAnnexes } from './annexes/hooks'
import { AuditLogTab } from './audit/AuditLogTab'
import { useContractFiles } from './files/hooks'
import { SalesPersonWidget } from './components/SalesPersonWidget'
import { AssigneeWidget } from './components/AssigneeWidget'
import { DiscussionsTab } from '@/features/collab/components/DiscussionsTab'
import { DiscussionsTabBadge } from '@/features/collab/components/DiscussionsTabBadge'
import { useAuthStore } from '@/features/auth/store'

// Wartości tabów = kotwice w URL (#dane itp.), żeby zakładkę dało się
// zlinkować i przeżyć odświeżenie strony.
const TAB_VALUES = [
  'data', 'elements', 'parties', 'tasks', 'statuses',
  'financing', 'files', 'annexes', 'discussions', 'audit',
] as const

function isTab(v: string): boolean {
  return (TAB_VALUES as readonly string[]).includes(v)
}

export function ContractDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const currentUserId = useAuthStore(s => s.user?.id ?? 0)
  const navigate = useNavigate()
  const location = useLocation()
  const contractQ = useContract(id)
  const schemaQ = useContractFormSchema(contractQ.data ? contractQ.data.configurationCode : null)
  const annexesQ = useAnnexes(id, false)
  const annexes = annexesQ.data ?? []
  const hasActiveDraft = annexes.some(a => a.status === 'DRAFT')
  const publishedCount = annexes.filter(a => a.status === 'PUBLISHED').length
  const filesQ = useContractFiles(id)
  const filesCount = filesQ.data?.length ?? 0
  const elementsQ = useContractElements(id)
  const elementsCount = elementsQ.data?.length ?? 0
  const [cancelOpen, setCancelOpen] = useState(false)

  const hashTab = location.hash.replace(/^#/, '')
  const [activeTab, setActiveTab] = useState<string>(() => (isTab(hashTab) ? hashTab : 'data'))

  // Sync przy nawigacji wstecz/naprzód (zmiana hasha spoza klików w taby).
  useEffect(() => {
    if (isTab(hashTab) && hashTab !== activeTab) setActiveTab(hashTab)
  }, [hashTab, activeTab])

  function changeTab(value: string) {
    setActiveTab(value)
    navigate({ hash: value }, { replace: true })
  }

  useEffect(() => {
    if (contractQ.error && (contractQ.error as any)?.response?.status === 404) {
      navigate('/contracts')
    }
  }, [contractQ.error, navigate])

  if (contractQ.isLoading || schemaQ.isLoading) return <div className="p-4">Ładowanie umowy...</div>
  if (contractQ.error) return <div className="p-4 text-red-600">Błąd: {String(contractQ.error)}</div>
  if (!contractQ.data || !schemaQ.data) return null

  return (
    // -m-* niweluje p-4/p-6/p-8 z MainLayout <main>: detail jest "fullbleed",
    // własne sub-headery same dbają o padding wewnątrz.
    <div className="flex flex-col w-full h-full -m-4 md:-m-6 lg:-m-8">
      <DetailHeader
        title={`Umowa ${contractQ.data.code}`}
        subtitle={contractQ.data.ordererName ?? 'Brak zamawiającego'}
        actions={
          <div className="flex items-center gap-4">
            <AssigneeWidget assigneeUserId={contractQ.data.assigneeUserId ?? null} />
            <SalesPersonWidget
              contractId={id}
              salesPersonId={contractQ.data.salesPersonId ?? null}
            />
          </div>
        }
      />
      <StageStepperHeader configurationCode={contractQ.data.configurationCode} currentStageCode={contractQ.data.currentStageCode} />
      <AnnexBanner contractId={id} onJumpToAnnexes={() => changeTab('annexes')} />
      <Tabs value={activeTab} onValueChange={changeTab} className="flex-1 flex flex-col gap-0 overflow-hidden">
        <TabsList className="rounded-none w-full justify-start gap-1 px-6 border-b border-slate-200 bg-white h-auto py-0 shadow-none">
          {[
            { v: 'data', l: 'Dane', badge: null as React.ReactNode },
            {
              v: 'elements',
              l: 'Produkty',
              badge:
                elementsCount > 0 ? (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-700">
                    {elementsCount}
                  </span>
                ) : null,
            },
            { v: 'financing', l: 'Finansowanie', badge: null },
            { v: 'parties', l: 'Osoby', badge: null },
            { v: 'tasks', l: 'Zadania', badge: null },
            { v: 'statuses', l: 'Statusy', badge: null },
            {
              v: 'files',
              l: 'Pliki',
              badge:
                filesCount > 0 ? (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-700">
                    {filesCount}
                  </span>
                ) : null,
            },
            {
              v: 'annexes',
              l: 'Aneksy',
              badge:
                publishedCount > 0 || hasActiveDraft ? (
                  <span className="ml-1.5 inline-flex items-center gap-1">
                    {publishedCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-sky-100 text-sky-800">
                        {publishedCount}
                      </span>
                    )}
                    {hasActiveDraft && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-amber-500"
                        title="Aktywny draft"
                      />
                    )}
                  </span>
                ) : null,
            },
            { v: 'discussions', l: 'Dyskusje', badge: <DiscussionsTabBadge entityRef={`easy:contract:${id}`} /> },
            { v: 'audit', l: 'Historia', badge: null },
          ].map(({ v, l, badge }) => (
            <TabsTrigger
              key={v}
              value={v}
              className="relative px-4 py-3 text-sm font-medium text-slate-500 hover:text-slate-900 rounded-none shadow-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-sky-700 data-[state=active]:font-semibold after:absolute after:left-2 after:right-2 after:-bottom-px after:h-0.5 after:bg-sky-600 after:rounded-full after:opacity-0 data-[state=active]:after:opacity-100 transition-colors"
            >
              {l}
              {badge}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="data" className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-auto bg-slate-50">
            <SectionTabs contract={contractQ.data} schema={schemaQ.data} />
          </main>
          <div className="w-80 shrink-0 border-l bg-slate-50 overflow-y-auto flex flex-col">
            <TaskSidebar
              contract={contractQ.data}
              onCancel={() => setCancelOpen(true)}
            />
            <ContractSidebarSections contract={contractQ.data} schema={schemaQ.data} />
          </div>
        </TabsContent>
        <TabsContent value="elements" className="flex-1 overflow-auto bg-slate-50">
          <ElementsTab contractId={id} />
        </TabsContent>
        <TabsContent value="parties" className="flex-1 overflow-auto bg-slate-50">
          <ContractPartiesPanel contractId={id} />
        </TabsContent>
        <TabsContent value="tasks" className="flex-1 overflow-auto bg-slate-50">
          <ContractTasksTab
            contractId={id}
            contractLabel={`Umowa ${contractQ.data.code}${contractQ.data.ordererName ? ' - ' + contractQ.data.ordererName : ''}`}
          />
        </TabsContent>
        <TabsContent value="statuses" className="flex-1 overflow-auto bg-slate-50">
          <StatusesTab contractId={id} />
        </TabsContent>
        <TabsContent value="financing" className="flex-1 overflow-auto bg-slate-50">
          <FinancingTab contractId={id} />
        </TabsContent>
        <TabsContent value="files" className="flex-1 overflow-auto bg-slate-50">
          <FilesTab contractId={id} configurationCode={contractQ.data.configurationCode} />
        </TabsContent>
        <TabsContent value="annexes" className="flex-1 overflow-auto bg-slate-50">
          <AnnexesTab
            contractId={id}
            contractVersion={contractQ.data.version}
            contractExtras={contractQ.data.extras ?? {}}
          />
        </TabsContent>
        <TabsContent value="discussions" className="flex-1 overflow-hidden bg-slate-50">
          <DiscussionsTab entityRef={`easy:contract:${id}`} currentUserId={currentUserId} />
        </TabsContent>
        <TabsContent value="audit" className="flex-1 overflow-auto bg-slate-50">
          <AuditLogTab contractId={id} />
        </TabsContent>
      </Tabs>
      <CancelContractDialog
        contractId={id}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      />
    </div>
  )
}
